<!--
  DateInput Component
  Encapsulates date input with ISO string (YYYY-MM-DD) as value.
  Renders a text input formatted per the user's date format setting
  (European/Us/Iso8601, see lib/settings.ts) plus a calendar-icon button that
  opens a hidden native <input type="date"> for picking.
  Typed text is parsed in the user's format; ISO (YYYY-MM-DD) is always
  additionally accepted so paste and programmatic ISO input keep working.
-->
<script context="module" lang="ts">
  // Module-level counter so every mounted instance gets a unique id for the
  // aria-describedby error link (U3/P1), without a prop the caller has to supply.
  let idCounter = 0;
</script>

<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { _ } from 'svelte-i18n';
  import Calendar from 'lucide-svelte/icons/calendar';
  import { userSettings } from '../../stores/settingsStore';
  import type { DateFormatType } from '../../settings';

  /** ISO date string (YYYY-MM-DD) or empty string */
  export let value = '';
  /** Earliest accepted ISO date (Spec 141 E11): an earlier date typed or picked is replaced by it when the field is left. */
  export let min = '';
  export let placeholder = '';
  /** Additional CSS classes for the text input itself */
  export let inputClass = '';
  /** Additional CSS classes for the wrapper (U6) — "class" is a reserved word. */
  let clazz = '';
  export { clazz as class };

  const dispatch = createEventDispatcher<{ change: string }>();
  const uid = `date-input-${++idCounter}`;
  const errorId = `${uid}-error`;

  let text = '';
  let invalid = false;
  let lastValue: string | undefined;
  let lastFormat: DateFormatType | undefined;
  let pickerEl: HTMLInputElement;

  function pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  /** Validates a calendar date (rejects e.g. 31.02.) and returns ISO YYYY-MM-DD, or null. */
  function toIso(year: number, month: number, day: number): string | null {
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }
    return `${year}-${pad(month)}-${pad(day)}`;
  }

  // U2: day/month one or two digits, year two (→ 20xx) or four digits; separator is the
  // format's usual one ("." European / "/" Us) plus "," and whitespace (review U2).
  function parseInput(raw: string, format: DateFormatType): string | null {
    const trimmed = raw.trim();
    const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return toIso(Number(iso[1]), Number(iso[2]), Number(iso[3]));

    if (format === 'European') {
      const m = trimmed.match(/^(\d{1,2})[.,\s]+(\d{1,2})[.,\s]+(\d{2}|\d{4})$/);
      if (m) return toIso(yearOf(m[3]), Number(m[2]), Number(m[1]));
    } else if (format === 'Us') {
      const m = trimmed.match(/^(\d{1,2})[/,\s]+(\d{1,2})[/,\s]+(\d{2}|\d{4})$/);
      if (m) return toIso(yearOf(m[3]), Number(m[1]), Number(m[2]));
    }
    return null;
  }

  function yearOf(raw: string): number {
    return raw.length === 2 ? 2000 + Number(raw) : Number(raw);
  }

  function formatForDisplay(iso: string, format: DateFormatType): string {
    if (!iso) return '';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return iso;
    const [, y, mo, d] = m;
    if (format === 'European') return `${d}.${mo}.${y}`;
    if (format === 'Us') return `${mo}/${d}/${y}`;
    return iso;
  }

  function placeholderKeyFor(format: DateFormatType): string {
    if (format === 'European') return 'components.date_input.placeholder_european';
    if (format === 'Us') return 'components.date_input.placeholder_us';
    return 'components.date_input.placeholder_iso';
  }

  // Re-format the displayed text whenever the external value or the user's date
  // format setting changes. Skipped for changes this component made itself
  // (commitValid keeps lastValue in sync), so typing isn't fought mid-edit.
  $: if (value !== lastValue || $userSettings.dateFormat !== lastFormat) {
    lastValue = value;
    lastFormat = $userSettings.dateFormat;
    text = formatForDisplay(value, $userSettings.dateFormat);
    invalid = false;
  }

  function commitValid(iso: string) {
    value = iso;
    lastValue = iso;
    text = formatForDisplay(iso, $userSettings.dateFormat);
    invalid = false;
    dispatch('change', iso);
  }

  function handleInput(e: Event) {
    text = (e.target as HTMLInputElement).value;
    // L5: typing optimistically clears the error — re-validation happens on blur, not on
    // every keystroke, so the red state doesn't "stick" while the user is still typing.
    invalid = false;
    if (text === '') {
      commitValid('');
      return;
    }
    const parsed = parseInput(text, $userSettings.dateFormat);
    if (parsed) commitValid(parsed);
  }

  function handleBlur() {
    if (text === '') {
      invalid = false;
      return;
    }
    const parsed = parseInput(text, $userSettings.dateFormat);
    if (parsed) {
      commitValid(min && parsed < min ? min : parsed);
      return;
    }
    // U1 (blocker): leaving the field with unparsable, non-empty text must never leave a
    // stale date around — clear it and dispatch the clear, but (unlike commitValid) keep the
    // error state visible so the user knows why it was cleared.
    text = '';
    value = '';
    lastValue = '';
    invalid = true;
    dispatch('change', '');
  }

  function openPicker() {
    if (typeof pickerEl?.showPicker === 'function') {
      try {
        pickerEl.showPicker();
        return;
      } catch {
        // fall through to click fallback (e.g. unsupported in test/jsdom env)
      }
    }
    // L3/P2: no .focus() here — the picker is aria-hidden, and focusing an element hidden
    // from assistive tech is itself an a11y bug. A plain .click() is enough to open the
    // native picker in browsers without showPicker() support.
    pickerEl?.click();
  }

  function handlePickerChange(e: Event) {
    const picked = (e.target as HTMLInputElement).value;
    commitValid(min && picked && picked < min ? min : picked);
  }
</script>

<div class="relative w-full {clazz}">
  <input
    type="text"
    inputmode="decimal"
    value={text}
    placeholder={placeholder || $_(placeholderKeyFor($userSettings.dateFormat))}
    on:input={handleInput}
    on:blur={handleBlur}
    on:keydown
    aria-invalid={invalid ? 'true' : undefined}
    aria-describedby={invalid ? errorId : undefined}
    class="w-full pl-2.5 pr-8 py-1.5 bg-bg-alt border rounded text-sm text-text focus:outline-none focus:ring-1 {invalid
      ? 'border-error focus:border-error focus:ring-error/30'
      : 'border-border focus:border-accent focus:ring-accent/30'} {inputClass}"
    {...$$restProps}
  />
  <button
    type="button"
    class="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-accent transition-colors cursor-pointer border-none bg-transparent"
    aria-label={$_('components.date_input.open_calendar')}
    title={$_('components.date_input.open_calendar')}
    on:click={openPicker}
  >
    <Calendar class="w-4 h-4" strokeWidth={1.5} />
  </button>
  <input
    type="date"
    bind:this={pickerEl}
    {value}
    min={min || undefined}
    on:change={handlePickerChange}
    tabindex="-1"
    aria-hidden="true"
    class="sr-only"
  />
  {#if invalid}
    <p id={errorId} class="text-xs text-error mt-1" data-testid="date-input-error">
      {$_('components.date_input.invalid_error', {
        values: { format: $_(placeholderKeyFor($userSettings.dateFormat)) },
      })}
    </p>
  {/if}
</div>
